import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ContainerProvider, type Container } from '@/application/container';
import { HttpAuthGateway } from '@/infrastructure/http/repositories/HttpAuthGateway';
import { HttpKitRepository } from '@/infrastructure/http/repositories/HttpKitRepository';
import { HttpJobOrderRepository } from '@/infrastructure/http/repositories/HttpJobOrderRepository';
import { HttpArchiveRepository } from '@/infrastructure/http/repositories/HttpArchiveRepository';
import { HttpBoardRepository } from '@/infrastructure/http/repositories/HttpBoardRepository';
import { HttpClientRepository } from '@/infrastructure/http/repositories/HttpClientRepository';
import { HttpGeoRepository } from '@/infrastructure/http/repositories/HttpGeoRepository';
import { HttpNotificationRepository } from '@/infrastructure/http/repositories/HttpNotificationRepository';
import { HttpPreferencesRepository } from '@/infrastructure/http/repositories/HttpPreferencesRepository';
import { HttpFinanceRepository } from '@/infrastructure/http/repositories/HttpFinanceRepository';
import { BrowserDeviceNotifier } from '@/infrastructure/device/BrowserDeviceNotifier';
import { HttpReportRepository } from '@/infrastructure/http/repositories/HttpReportRepository';
import { HttpActivityRepository } from '@/infrastructure/http/repositories/HttpActivityRepository';
import { HttpAdminRepository } from '@/infrastructure/http/repositories/HttpAdminRepository';
import { createOutboxQueue } from '@/infrastructure/offline/createOutboxQueue';
import { OutboxReplay } from '@/infrastructure/offline/replay';
import { LocalInstallPreferences } from '@/infrastructure/storage/LocalInstallPreferences';
import { LocalTablePreferences } from '@/infrastructure/storage/LocalTablePreferences';
import { LibPhoneNumberBook } from '@/infrastructure/phone/LibPhoneNumberBook';
import { ServiceWorkerAppUpdater } from '@/infrastructure/pwa/ServiceWorkerAppUpdater';
import { ZxingQrEncoder } from '@/infrastructure/qr/ZxingQrEncoder';
import { CameraScanner } from '@/infrastructure/scanner/CameraScanner';
import { SystemClock } from '@/infrastructure/time/SystemClock';
import { BrowserLocationProvider } from '@/infrastructure/location/BrowserLocationProvider';
import { ApiError } from '@/infrastructure/http/client';
import { env } from '@/shared/config/env';
import { SseLiveStream } from '@/infrastructure/realtime/SseLiveStream';

/**
 * Section 8.4. One provider composes the container. In production it wires the
 * HTTP repositories; a test wires fakes, or lets MSW intercept and keeps these,
 * which is preferred because it exercises the serialisation too.
 */
export function buildContainer(): Container {
  // One instance serves both the job order and the print ports: they are the
  // same HTTP surface and splitting the class would only duplicate the client.
  const jobOrders = new HttpJobOrderRepository();
  // The replay reissues stored commands through the same repository the screens
  // use, so there is one code path to the API and one place it can be wrong.
  const kits = new HttpKitRepository();
  const outbox = createOutboxQueue();

  return {
    auth: new HttpAuthGateway(),
    clock: new SystemClock(),
    location: new BrowserLocationProvider(),
    kits,
    jobOrders,
    print: jobOrders,
    archive: new HttpArchiveRepository(),
    clients: new HttpClientRepository(),
    geo: new HttpGeoRepository(),
    phones: new LibPhoneNumberBook(),
    board: new HttpBoardRepository(),
    notifications: new HttpNotificationRepository(),
    preferences: new HttpPreferencesRepository(),
    finance: new HttpFinanceRepository(),
    deviceNotifier: new BrowserDeviceNotifier(),
    reports: new HttpReportRepository(),
    activity: new HttpActivityRepository(),
    admin: new HttpAdminRepository(),
    outbox,
    outboxSync: new OutboxReplay(outbox, kits),
    installPreferences: new LocalInstallPreferences(),
    tablePreferences: new LocalTablePreferences(),
    appUpdater: new ServiceWorkerAppUpdater(env.appVersion),
    qr: new ZxingQrEncoder(),
    scanner: new CameraScanner(),
    live: new SseLiveStream(),
  };
}

export function buildQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Section 8.2. Lists are stale after thirty seconds; the floor board
        // overrides this to five where it is used.
        staleTime: 30_000,
        // Two retries with backoff, but never on a 4xx: a 403 does not become
        // a 200 by asking again, and retrying it three times on a lab phone
        // just spends battery.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        // This is what makes a phone coming out of a pocket show current data.
        refetchOnWindowFocus: true,
        /**
         * Section 9, caching.
         *
         * With the default `online` mode TanStack does not issue the request at
         * all when the browser reports no network: the query sits paused and a
         * screen the technician has not opened before shows a spinner with no
         * end and no explanation. It also makes the Workbox network-first
         * fallback cache for kits, stages and catalogue unreachable, because
         * nothing ever asks for them, which would leave that whole config as
         * dead weight.
         *
         * `always` lets the request run. The service worker answers it from
         * cache where it can, and where it cannot the query fails into a real
         * error state that the screen can render honestly.
         */
        networkMode: 'always',
      },
      mutations: {
        retry: false,
        /**
         * Section 9, and the reason it is not the default.
         *
         * TanStack's default `networkMode: 'online'` PAUSES a mutation when the
         * browser reports no network: `onMutate` runs, `mutationFn` never does,
         * and the command sits in memory until the connection returns. For this
         * application that is the exact failure the outbox exists to prevent. A
         * technician marks a case done in the furnace corner, the phone dies,
         * and the work is gone with no record that it ever happened.
         *
         * `always` runs the mutation regardless. The send fails, the outbox has
         * already recorded it durably, and the replay sends it on reconnect.
         * The queue is our durability layer, so the library's in-memory one is
         * both redundant and harmful.
         */
        networkMode: 'always',
      },
    },
  });
}

export function AppProviders({
  children,
  container,
  queryClient,
}: {
  children: ReactNode;
  container?: Container;
  queryClient?: QueryClient;
}) {
  const [client] = useState(() => queryClient ?? buildQueryClient());
  const [wired] = useState(() => container ?? buildContainer());

  return (
    <QueryClientProvider client={client}>
      <ContainerProvider container={wired}>{children}</ContainerProvider>
    </QueryClientProvider>
  );
}
